import React, { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../utils/Context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import PLUS_ICON from "../../assets/plus_icon.svg";
import SEND_ARROW from "../../assets/sendArrow.svg";
import { recognizeSpeech } from "../../utils/services/speechService";
import { v4 as uuidv4 } from "uuid";
import { multiChatAllModels, newSession } from "../../utils/services/api";
import { saveModelResponsePatch } from "../../utils/helpers/modelResponsesStorage";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa6";
import { useMsal } from "@azure/msal-react";

const InputFooter = ({
  setMessages,
  conversationId,
  loading,
  setLoading,
  messages,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { appData, dispatch } = useContext(AppContext);
  const appDataRef = useRef(appData);
  // Tracks which conversation is currently on screen, read fresh inside async
  // callbacks (a plain closure over `conversationId` would go stale the moment
  // the user navigates to a different chat while a request is still in flight).
  const conversationIdRef = useRef(conversationId);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);
  const [inputText, setInputText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [partialBuffer, setPartialBuffer] = useState("");
  const { accounts } = useMsal();

  const userData = accounts[0];
  const textareaRef = useRef(null);

  const handleVoiceClick = () => {
    if (isRecording) {
      // 🔴 Stop recording
      recognizeSpeech({ action: "stop", onStop: () => setIsRecording(false) });
    } else {
      // 🟢 Start fresh recording
      setPartialBuffer(""); // clear partials
      setInputText(""); // clear previous transcript ✅
      setIsRecording(true);

      recognizeSpeech({
        action: "start",
        onRecognizing: (text) => setPartialBuffer(text),
        onRecognized: (finalText) => {
          // only set the new recognized text (no append)
          setInputText((prev) => (prev ? prev + " " + finalText : finalText));
          setPartialBuffer("");
        },
        onStop: () => setIsRecording(false),
        onError: (error) => {
          console.error("Speech error:", error);
          setIsRecording(false);
        },
      });
    }
  };

  const handleSubmit = async (query) => {
    // Stop recording if active
    if (isRecording) {
      recognizeSpeech({
        action: "stop",
        onStop: () => setIsRecording(false),
      });
    }

    if (query !== "") {
      setInputText("");

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      let time = new Date();
      let message_id = uuidv4();
      // Treat as a new conversation whenever there's no valid EXISTING conversation
      // to send into — either the explicit "?new_conversation" marker, or simply no
      // conversationId at all (e.g. landing on a bare /chat URL directly, not via
      // "+ New Chat" or clicking a sidebar item). Without this fallback, session_id
      // ends up null in that case, which the backend's payload validation rejects
      // outright (422) — every endpoint fails, not because of any state-sync bug,
      // but because there was never a valid session to send.
      const isNewConversation =
        window.location.search.includes("?new_conversation") || !conversationId;

      let session_id = isNewConversation ? appData?.newSessionId : conversationId;
      if (isNewConversation && !session_id) {
        session_id = await newSession();
        dispatch({ type: "NEW_SESSION_ID", payload: session_id });
      }

      console.log("[Phase3] handleSubmit", { isNewConversation, session_id, message_id, conversationIdProp: conversationId, newSessionId: appData?.newSessionId });

      const userMsg = { from: "User", message: query, time, message_id };
      // Seed the placeholder bot turn in the SAME dispatch as the user message
      // (not a separate one moments later in handleQuery) — two back-to-back
      // dispatches left a window where the second one read a stale, pre-first-
      // dispatch snapshot of appData and silently clobbered it. One atomic seed
      // removes that race entirely.
      const placeholderBotMsg = {
        from: "Bot",
        msg_id_log: { msg_id: message_id },
        modelResponses: {},
        activeModel: null,
      };

      if (isNewConversation) {
        // Give the new chat a first-class entry in the sidebar/appData store
        // immediately, and jump the URL to its real conversation_id right away —
        // don't wait for a model response. This means the sidebar is instantly
        // navigable to any other chat, and background model updates below always
        // target this session_id rather than "whatever the user happens to be
        // viewing right now". Dispatched as SEED_CONVERSATION (reducer-side
        // prepend) rather than a locally-computed array — see the reducer's
        // comment for why that distinction matters when several of these can
        // race against the per-model updates fired moments later.
        const newConvo = {
          session_id,
          session_name: query.length > 40 ? `${query.slice(0, 40)}…` : query,
          chat_history: [userMsg, placeholderBotMsg],
          date_created: time,
          is_deleted: false,
        };
        dispatch({ type: "SEED_CONVERSATION", payload: newConvo });
        navigate(`/chat?conversation_id=${session_id}`);
        // Reflect immediately in the visible message list too — don't wait for
        // the URL/prop change to round-trip back down before showing anything.
        setMessages([userMsg, placeholderBotMsg]);
        conversationIdRef.current = session_id;
      } else {
        // Reducer-side append (APPEND_TO_CONVERSATION) — see SEED_CONVERSATION's
        // comment above for why this can't be a locally-computed array. Also
        // mirror straight into the visible `messages` state (matching the
        // existing refreshLikeState pattern elsewhere in this file) so the
        // user's own message shows immediately.
        dispatch({
          type: "APPEND_TO_CONVERSATION",
          payload: { session_id: conversationId, messages: [userMsg, placeholderBotMsg] },
        });
        setMessages((prev) => [...prev, userMsg, placeholderBotMsg]);
      }

      const state = localStorage.getItem("state");

      const checkMemberShip =
        userData?.idTokenClaims?.extension_NECAHasActiveMembership;

      handleQuery(query, time, message_id, state, checkMemberShip, session_id);
    }
  };

  const handleChange = (e) => {
    setInputText(e.target.value);
  };

  const handleQuery = async (
    item,
    time,
    message_id,
    state,
    checkMemberShip,
    session_id
  ) => {
    dispatch({ type: "botMessageRunning", payload: true });
    dispatch({ type: "isMultiChatRunning", payload: true });
    setLoading(true);

    // Phase-3: fan the question out to all 8 models simultaneously (see MODEL_LIST
    // in utils/services/api.js). The placeholder bot turn was already seeded by
    // handleSubmit (atomically with the user message) — here we just fill in each
    // model's slot as it settles, so whichever model answers first is shown
    // without waiting on the rest. Each update dispatches APPLY_MODEL_UPDATE,
    // which does the find-and-patch INSIDE the reducer against the true current
    // state (see AppContext.js's reducer comment) — 8 of these can land in a
    // tight burst (models that fail fast all settle within milliseconds of each
    // other) and must not race each other or the seeding dispatch above; a
    // locally-computed "next array" read from a ref/closure cannot guarantee
    // that the way a reducer can. It's also mirrored into the visible `messages`
    // state directly, but ONLY when session_id still matches whatever
    // conversation is currently on screen (conversationIdRef) — so a background
    // update for a chat the user has since navigated away from updates the
    // durable store (ready for whenever they navigate back) without clobbering
    // whatever conversation they're actually looking at right now.
    const updateModel = (modelValue, patch) => {
      dispatch({
        type: "APPLY_MODEL_UPDATE",
        payload: { session_id, message_id, modelValue, patch },
      });
      // Best-effort client cache so this question's dropdown survives a full
      // page refresh (the backend has no concept of per-model answers, so
      // there's nothing server-side to restore this from otherwise).
      saveModelResponsePatch(message_id, modelValue, patch);

      if (conversationIdRef.current === session_id) {
        setMessages((prev) =>
          prev.map((m) => {
            if (m?.msg_id_log?.msg_id !== message_id) return m;
            const nextModelResponses = {
              ...m.modelResponses,
              [modelValue]: { ...m.modelResponses?.[modelValue], ...patch },
            };
            const nextActiveModel =
              !m.activeModel && patch.status === "done" ? modelValue : m.activeModel;
            return { ...m, modelResponses: nextModelResponses, activeModel: nextActiveModel };
          })
        );
      }
    };

    setLoading(false);

    try {
      await multiChatAllModels(
        item,
        messages,
        time,
        session_id,
        message_id,
        state,
        updateModel
      );

      // Background refresh only — picks up the real session_name/history from the
      // backend once it's caught up. Doesn't block anything above, and the merge
      // logic in DataDrivenChat's effect preserves modelResponses across this.
      appDataRef.current.getConvo();

      dispatch({ type: "botMessageRunning", payload: false });
      dispatch({ type: "isMultiChatRunning", payload: false });
      dispatch({ type: "GLOBAL_LOADING", payload: false });
    } catch (err) {
      dispatch({ type: "botMessageRunning", payload: false });
      dispatch({ type: "isMultiChatRunning", payload: false });
      console.log("err", err);
      // Per-model failures are already captured in modelResponses via updateModel;
      // this only catches something unexpected outside the per-model fan-out itself.
    }
  };

  // Guards the effect below from redundantly re-invoking handleCreateNewChat
  // when ?new_conversation shows up in the URL purely because THIS function
  // just put it there — only auto-create when ?new_conversation is present
  // because the user arrived via an external link (e.g. the "Chat" links on
  // Settings/Activity Log) that never called handleCreateNewChat at all.
  const selfNavigatedRef = useRef(false);

  // Captures whatever the user had typed but not yet sent, right when
  // "+ New Chat" is clicked, so it can be restored into the new chat's
  // input instead of being cleared like every other chat switch.
  const draftToRestoreRef = useRef(null);

  const handleCreateNewChat = async () => {
    try {
      const newSessionId = await newSession();
      dispatch({
        type: "NEW_SESSION_ID",
        payload: newSessionId,
      });
      selfNavigatedRef.current = true;
      navigate(`${window.location.pathname}?new_conversation`);
      setMessages([]);
    } catch (err) {
      console.log(err);
    }
  };

  const handleNewChatClick = () => {
    if (inputText.trim() !== "") {
      draftToRestoreRef.current = inputText;
    }
    handleCreateNewChat();
  };

  useEffect(() => {
    if (draftToRestoreRef.current !== null) {
      setInputText(draftToRestoreRef.current);
      draftToRestoreRef.current = null;
    } else {
      setInputText("");
    }

    if (selfNavigatedRef.current) {
      selfNavigatedRef.current = false;
      return;
    }

    if (window.location.search.includes("?new_conversation")) {
      handleCreateNewChat();
    }
  }, [window.location.pathname, searchParams]);

  useEffect(() => {
    appDataRef.current = appData;
  }, [appData]);

  return (
    <div className="message-footer-container min-h-[60px] shrink-0 bg-[#F6F8FB] mt-[10px] py-2">
      <div
        className={`flex items-center gap-2 w-full m-auto rounded-[10px] bg-[#F6F8FB] px-4 ${
          messages?.length !== 0 ? "w-[89%] md:w-[85%]" : "w-[89%]"
        }`}
      >
        <div className="relative block sm:hidden">
          {isRecording && (
            <span className="absolute inset-0 animate-ping rounded-full border border-[#00AEEF] pointer-events-none"></span>
          )}
          <button
            onClick={handleVoiceClick}
            className="z-10 flex items-center justify-center h-[35px] w-[35px] rounded-full hover:bg-[#e9e8e8] transition duration-300"
          >
            {isRecording ? (
              <FaMicrophoneSlash className="text-[14px]" color="#6D6D6D" />
            ) : (
              <FaMicrophone className="text-[18px]" color="#6D6D6D" />
            )}
          </button>
        </div>

        {/* White input container */}
        <div className="input w-full flex items-center rounded-full bg-white shadow-[0px_1px_5.3px_0px_#00000021] overflow-hidden ps-4 pe-2 py-2">
          <form
            className="w-full flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(inputText);
            }}
          >
            {/* Autosizing textarea (up to 3 lines) */}
            <textarea
              ref={textareaRef}
              rows={1}
              className="flex-1 resize-none outline-0 text-sm placeholder:text-sm bg-transparent px-3 py-2 leading-5
             max-h-[56px] overflow-y-auto"
              name="input"
              onChange={handleChange}
              // Existing chats (already have history loaded) get a reminder to
              // keep follow-ups on-topic here vs. starting a New Chat for
              // something unrelated — a brand-new, empty chat just gets the
              // plain default prompt.
              placeholder={
                messages?.length > 0
                  ? "Continue the topic here. For a new topic, click New Chat"
                  : "Write your message here"
              }
              value={partialBuffer || inputText}
              autoComplete="off"
              disabled={loading || appData?.botMessageRunning}
              onInput={(e) => {
                e.currentTarget.style.height = "auto"; // reset height
                const maxHeight = 56; // cap at 2 lines
                e.currentTarget.style.height = `${Math.min(
                  e.currentTarget.scrollHeight,
                  maxHeight
                )}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(inputText); // send
                }
              }}
            />

            {/* Send button stays centered */}
            <button
              type="submit"
              className="shrink-0 h-9 w-9 flex items-center justify-center rounded-full hover:bg-[#F2F4F7]"
              disabled={loading || appData?.botMessageRunning}
            >
              <img className="w-[22px]" src={SEND_ARROW} alt="Send" />
            </button>
          </form>
        </div>

        <button
          onClick={handleNewChatClick}
          className="hidden sm:flex w-[120px] md:w-[140px] bg-[#00AEEF] hover:bg-[#3aa7cf] text-white items-center justify-center rounded-full gap-2 text-[13px] h-[30px] px-[13px] py-[18px] my-auto"
          disabled={loading || appData?.botMessageRunning}
        >
          <img className="h-[10px]" src={PLUS_ICON} alt="" />
          <p className="text-white">New Chat</p>
        </button>
      </div>
    </div>
  );
};

export default InputFooter;
